import React from "react";
import { Link } from "react-router-dom";
import { BrandMark, BrandWordmark } from "@/components/BrandMark";
import {
  Landmark, Pickaxe, Compass, ShieldCheck, Sparkles, Globe2, ArrowRight,
  MapPin, ScrollText, Users, Activity, Lock,
} from "lucide-react";

const Feature = ({ icon: Icon, title, text }) => (
  <div className="cad-panel p-5 group">
    <div className="w-9 h-9 rounded-sm cad-glass flex items-center justify-center mb-4">
      <Icon size={18} className="text-[#C99A2E]" strokeWidth={1.6} />
    </div>
    <div className="font-display font-bold text-[15px] text-[#F6F8FA] mb-1">{title}</div>
    <div className="text-[13px] text-[#B3C2CC] leading-relaxed">{text}</div>
  </div>
);

const Metric = ({ value, label }) => (
  <div className="cad-panel px-5 py-4">
    <div className="cad-kpi-value text-3xl text-[#C99A2E]">{value}</div>
    <div className="text-[10px] uppercase tracking-[0.24em] font-mono-cad text-[#7C93A5] mt-1">{label}</div>
  </div>
);

export const Landing = () => {
  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="cad-topo"></div>
      <div className="cad-vein"></div>
      {/* Nav */}
      <header className="relative z-10 flex items-center justify-between px-6 lg:px-12 pt-8 pb-2">
        <BrandMark size={168} />
        <nav className="flex items-center gap-3">
          <Link to="/portal" data-testid="cta-public-portal" className="cad-btn-outline px-4 py-2 rounded-sm text-[13px]">Public Portal</Link>
          <Link to="/login" data-testid="cta-login" className="cad-btn-primary px-4 py-2 rounded-sm text-[13px]">Sign in</Link>
        </nav>
      </header>

      {/* Hero */}
      <section className="relative z-10 px-6 lg:px-12 pt-10 pb-8 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
        <div className="lg:col-span-8">
          <div className="cad-chip cad-chip-gold mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-[#E1B34C]"></span>
            Sovereign Capability Demonstration · Zimbabwe
          </div>
          <h1 className="font-display font-bold text-[#F6F8FA] text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
            The National Cadastre.<br />
            <span className="text-[#C99A2E]">Land</span> · <span className="text-[#7CBFDC]">Survey</span> · <span className="text-[#6BB88A]">Mineral Rights</span>
          </h1>
          <p className="mt-5 text-[15px] lg:text-base text-[#B3C2CC] leading-relaxed max-w-2xl">
            Cadastre360™ is a sovereign-ready digital platform for registering, mapping, licensing, governing, monitoring
            and protecting land parcels, property interests, survey records and mineral rights across Zimbabwe —
            engineered from executive command down to public counter service.
          </p>
          <p className="mt-3 text-[13px] font-mono-cad uppercase tracking-[0.16em] text-[#7C93A5] max-w-2xl">
            Designed to meet and exceed leading international cadastral and mineral-rights administration benchmarks.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/login" data-testid="hero-cta-primary" className="cad-btn-primary px-6 py-3 rounded-sm text-sm inline-flex items-center gap-2">
              Enter Executive Workspace <ArrowRight size={16} />
            </Link>
            <Link to="/portal" data-testid="hero-cta-secondary" className="cad-btn-outline px-6 py-3 rounded-sm text-sm inline-flex items-center gap-2">
              Public Search & Verification
            </Link>
          </div>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-2xl">
            <Metric value="10" label="Provinces" />
            <Metric value="600+" label="Demo Parcels" />
            <Metric value="420" label="Mineral Rights" />
            <Metric value="140" label="Applications" />
          </div>
        </div>

        <div className="lg:col-span-4 relative">
          <div className="cad-panel p-6 relative overflow-hidden">
            <div className="absolute inset-0 opacity-30" style={{
              backgroundImage: "radial-gradient(circle at 20% 30%, rgba(201,154,46,0.28), transparent 55%)",
            }} />
            <div className="relative">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Activity size={18} className="text-[#C99A2E]" strokeWidth={1.7} />
                  <div className="font-display font-bold text-[15px] text-[#F6F8FA]">Executive Command</div>
                </div>
                <span className="cad-chip cad-chip-green text-[10px]">LIVE</span>
              </div>
              <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#7C93A5] mt-1">
                National Situation Room · Today's Snapshot
              </div>

              <div className="grid grid-cols-2 gap-2.5 mt-4">
                {[
                  { l: "Active Alerts",       v: "4",   tone: "gold" },
                  { l: "Pending Reviews",     v: "128", tone: "blue" },
                  { l: "Critical Conflicts",  v: "6",   tone: "red" },
                  { l: "Today's Approvals",   v: "31",  tone: "green" },
                ].map((k) => (
                  <div key={k.l} className="border cad-hairline rounded-sm px-3 py-2.5 bg-[#071B2B]/50">
                    <div className="font-mono-cad text-[9px] uppercase tracking-[0.18em] text-[#7C93A5]">{k.l}</div>
                    <div className="cad-kpi-value text-2xl mt-0.5" style={{
                      color: k.tone === "gold" ? "#E1B34C" : k.tone === "red" ? "#E67E7E"
                           : k.tone === "green" ? "#6BB88A" : "#7CBFDC",
                    }}>{k.v}</div>
                  </div>
                ))}
              </div>

              <div className="mt-4 space-y-1.5 text-[12px]">
                <div className="flex items-center justify-between">
                  <span className="text-[#DCE3E8]">Province status</span>
                  <span className="cad-chip cad-chip-green text-[10px]">10 online</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[#DCE3E8]">AI Executive Brief</span>
                  <span className="cad-chip cad-chip-gold text-[10px]">Ready</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[#DCE3E8]">System health</span>
                  <span className="cad-chip cad-chip-green text-[10px]">Nominal</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[#DCE3E8]">Digital signature service</span>
                  <span className="cad-chip cad-chip-green text-[10px]">Operational</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modules */}
      <section className="relative z-10 px-6 lg:px-12 py-10">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.28em] text-[#7C93A5]">Sector Modules</div>
            <h2 className="font-display text-2xl font-bold text-[#F6F8FA] mt-2">A complete national rights administration platform</h2>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Feature icon={Landmark} title="LandCadastre360™" text="Parcels, tenure, subdivision, consolidation, restrictions and historical geometry preserved through every legal act." />
          <Feature icon={Pickaxe} title="MiningCadastre360™" text="Prospecting licences, claims, leases, special grants with first-in-time priority and spatial-conflict detection." />
          <Feature icon={Compass} title="Survey360™" text="Surveyor register, diagrams, coordinates, examination and beacon monuments — all versioned and QR-verifiable." />
          <Feature icon={ScrollText} title="Registry360™" text="Titles, servitudes, mortgages, caveats, transfers and inheritance recorded with a full audit chain." />
          <Feature icon={ShieldCheck} title="Compliance360™" text="Work programmes, ground rental, environmental obligations and licence-expiry monitoring with escalations." />
          <Feature icon={Sparkles} title="Cadastre Intelligence" text="GPT-powered executive briefings, anomaly detection and duplicate-record identification for authorised officials." />
          <Feature icon={Globe2} title="Public Portal" text="Public parcel and mining-right search, application status tracking and QR certificate verification." />
          <Feature icon={Users} title="Community & Traditional Rights" text="First-class recognition of customary and communal land relationships with consultation evidence." />
        </div>
      </section>

      {/* Positioning */}
      <section className="relative z-10 px-6 lg:px-12 py-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="cad-panel p-6 lg:col-span-2">
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.28em] text-[#7C93A5]">Positioning</div>
          <h3 className="font-display text-xl font-bold text-[#F6F8FA] mt-2">Ready for national procurement, engineered for sovereignty</h3>
          <p className="text-[13.5px] text-[#B3C2CC] mt-3 leading-relaxed">
            Cadastre360™ is being developed as a Zimbabwe-ready national capability demonstration. Until officially procured,
            appointed or adopted by an authorised public authority, it is not claimed to be an official national register,
            nor is it endorsed by any ministry, the Surveyor-General, the Deeds Registry or any mining authority.
            The architecture supports government-hosted, private-cloud and approved sovereign-cloud deployment models.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="cad-chip">Government-hosted ready</span>
            <span className="cad-chip">Private cloud</span>
            <span className="cad-chip">Sovereign cloud</span>
            <span className="cad-chip">Data residency configurable</span>
          </div>
        </div>
        <div className="cad-panel p-6">
          <Lock size={20} className="text-[#C99A2E]" />
          <div className="font-display font-bold text-[#F6F8FA] mt-3">Independent Deployment</div>
          <div className="text-[13px] text-[#B3C2CC] mt-2 leading-relaxed">
            Cadastre360™ operates with its own authentication, database, sessions, branding and audit environment —
            architecturally governed by the THOM360™ Enterprise Platform Framework but never dependent on it at runtime.
          </div>
        </div>
      </section>

      <footer className="relative z-10 px-6 lg:px-12 py-8 border-t cad-hairline flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3">
        <div className="text-[11px] font-mono-cad uppercase tracking-[0.22em] text-[#5F7688]">
          Cadastre360™ · Zimbabwe National Cadastre Platform · Built on the THOM360™ Enterprise Platform Framework
        </div>
        <div className="text-[11px] font-mono-cad uppercase tracking-[0.22em] text-[#5F7688]">
          Demonstration Environment — Fictional and Synthetic Data
        </div>
      </footer>
    </div>
  );
};
